import React, { useState, useEffect } from 'react';
import { ShoppingCart, Package, AlertCircle, TrendingUp, DollarSign, Eye } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getGoogleAdsClient, getDateRange } from '../lib/google-ads-client';

interface ProductGroup {
  id: string;
  name: string;
  status: string;
  impressions: number;
  clicks: number;
  ctr: number;
  costMicros: number;
  conversions: number;
  conversionValue: number;
  roas: number;
}

export default function ShoppingCampaignManagerPage() {
  const [loading, setLoading] = useState(true);
  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [totalMetrics, setTotalMetrics] = useState({
    impressions: 0,
    clicks: 0,
    cost: 0,
    conversions: 0,
    revenue: 0,
    roas: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data: customerData } = await supabase
        .from('google_ads_customer_mapping')
        .select('*')
        .eq('is_active', true)
        .limit(1)
        .single();

      if (customerData) {
        setSelectedCustomerId(customerData.customer_id);
        await loadShoppingData(customerData.customer_id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadShoppingData = async (customerId: string) => {
    const range = getDateRange(30);
    const { data } = await supabase
      .from('google_ads_campaign_performance')
      .select('*')
      .eq('campaign_type', 'SHOPPING')
      .gte('date', range.startDate)
      .lte('date', range.endDate);

    if (data) {
      const groups = aggregateProductGroups(data);
      setProductGroups(groups);
      calculateTotalMetrics(groups);
    }
  };

  const aggregateProductGroups = (data: any[]): ProductGroup[] => {
    const grouped = new Map<string, any[]>();
    
    data.forEach(row => {
      const key = row.campaign_id;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)?.push(row);
    });

    return Array.from(grouped.entries()).map(([id, rows]) => {
      const totals = rows.reduce((acc, r) => ({
        impressions: acc.impressions + (r.impressions || 0),
        clicks: acc.clicks + (r.clicks || 0),
        costMicros: acc.costMicros + (r.cost_micros || 0),
        conversions: acc.conversions + (r.conversions || 0),
        conversionValue: acc.conversionValue + (r.conversion_value || 0)
      }), { impressions: 0, clicks: 0, costMicros: 0, conversions: 0, conversionValue: 0 });

      const cost = totals.costMicros / 1000000;
      const roas = cost > 0 ? totals.conversionValue / cost : 0;
      const ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;

      return {
        id,
        name: rows[0]?.campaign_name || 'Shopping Campaign',
        status: rows[0]?.campaign_status || 'ENABLED',
        impressions: totals.impressions,
        clicks: totals.clicks,
        ctr,
        costMicros: totals.costMicros,
        conversions: totals.conversions,
        conversionValue: totals.conversionValue,
        roas
      };
    });
  };

  const calculateTotalMetrics = (groups: ProductGroup[]) => {
    const totals = groups.reduce((acc, g) => ({
      impressions: acc.impressions + g.impressions,
      clicks: acc.clicks + g.clicks,
      cost: acc.cost + (g.costMicros / 1000000),
      conversions: acc.conversions + g.conversions,
      revenue: acc.revenue + g.conversionValue
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, revenue: 0 });

    setTotalMetrics({
      ...totals,
      roas: totals.cost > 0 ? totals.revenue / totals.cost : 0
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Shopping Campaign Manager</h1>
        <p className="text-gray-600 mt-1">Product feed optimization and shopping ad performance</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                ${totalMetrics.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">ROAS</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalMetrics.roas.toFixed(2)}x</p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Conversions</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalMetrics.conversions.toLocaleString()}</p>
            </div>
            <ShoppingCart className="w-8 h-8 text-purple-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Product Groups</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{productGroups.length}</p>
            </div>
            <Package className="w-8 h-8 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Product Groups Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Shopping Campaign Performance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Impressions</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Clicks</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">CTR</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Conversions</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">ROAS</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {productGroups.map((group) => (
                <tr key={group.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Package className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{group.name}</div>
                        <div className="text-xs text-gray-500">{group.status}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {group.impressions.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {group.clicks.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {group.ctr.toFixed(2)}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    ${(group.costMicros / 1000000).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {group.conversions}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    ${group.conversionValue.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <span className={`font-bold ${group.roas > 3 ? 'text-green-600' : group.roas > 1 ? 'text-blue-600' : 'text-red-600'}`}>
                      {group.roas.toFixed(2)}x
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
